import React, {useContext, useEffect, useRef, useState} from 'react';
import './App.css';
import './utils/GlobalErrorHandler'; // Add global error handler
import './utils/KeeperFix'; // Add Keeper Security fix
import ErrorBoundary from './utils/ErrorBoundary';
import {CsrfContext, CsrfProvider, useCsrf} from "./providers/CsrfProvider";
import Statements from './bundles/template/views/statement/Statements'
import ViewLanding from './bundles/landing/views/ViewLanding';
import ViewPrivacyPolicy from './bundles/landing/views/ViewPrivacyPolicy';
import ViewDashboard from "./bundles/dashboard/views/ViewDashboard";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import ViewNewTemplate from "./bundles/template/views/ViewNewTemplate";
import ViewTemplates from "./bundles/template/views/ViewTemplates";
import ViewDefaultBill from "./bundles/template/views/ViewDefaultBill";
import {ThemeProvider} from "./styles/ThemeProvider";
import { createContext } from 'react';
import {AuthProvider, AuthContext} from "./bundles/auth/providers/AuthProvider";
import {AuthEventHandler} from "./event_handlers/AuthEventHandler";
import MyProfileView from "./bundles/profile/views/MyProfileView";
import AuthPage from "./bundles/auth/views/ViewAuth";
import { LogoutPage } from "./bundles/auth/views/LogoutPage";
import InactivityForm from "./bundles/common/forms/InactivityForm";
import SessionTimeoutModal from "./bundles/auth/modals/ModalSessionTimeout";
import {Navigate, Route, Routes, BrowserRouter as Router, useLocation} from "react-router-dom";
import {GroupProvider} from "./providers/GroupProvider";
import { isSessionValid } from "./utils/auth/ClaimsParser";
import QuickActionsButton from "./bundles/common/components/QuickActionsButton";

export const AppBarContext = createContext();

const App = () => {
    return (
        <ErrorBoundary>
            <div className="App">
                <CsrfProvider>
                    <Router>
                        <AuthProvider>
                            <AuthEventHandler/>
                            <GroupProvider>
                                <ThemeProvider>
                                    <CssBaseline />
                                    {/*<Box component="main" sx={{flexGrow: 1, p: 3, height:'100vh', width:'100vw'}}>*/}
                                        <AppRoutes/>
                                    {/*</Box>*/}
                                </ThemeProvider>
                            </GroupProvider>
                        </AuthProvider>
                    </Router>
                </CsrfProvider>
            </div>
        </ErrorBoundary>
    );
}

const RedirectWithSearch: React.FC<{ to: string; state?: any }> = ({ to, state }) => {
    const location = useLocation();
    return <Navigate to={{ pathname: to, search: location.search }} state={state} replace />;
};

export const AppRoutes = () => {
    const {authClaims, showTimeoutModal, onStayLoggedIn, onLogout} = useContext(AuthContext);

    return (
        <>
            <SessionTimeoutModal
                open={showTimeoutModal}
                onStayLoggedIn={onStayLoggedIn}
                onSessionTimeout={onLogout}
            />
            <QuickActionsButton />
            <InactivityForm>
                <Routes>
                    {/* AUTH - Redirect routes */}
                    <Route path='/login' element={<Navigate to="/auth" state={{ initialTab: 'login' }} replace />} />
                    <Route path='/signin' element={<Navigate to="/auth" state={{ initialTab: 'login' }} replace />} />
                    <Route path='/signup' element={<Navigate to="/auth" state={{ initialTab: 'signup' }} replace />} />
                    <Route path='/join' element={<Navigate to="/auth" state={{ initialTab: 'signup' }} replace />} />
                    <Route path='/forgot' element={<Navigate to="/auth" state={{ initialTab: 'forgot' }} replace />} />
                    <Route path='/confirm' element={<RedirectWithSearch to="/auth" state={{ initialTab: 'confirm' }} />} />
                    <Route path='/verify' element={<RedirectWithSearch to="/auth" state={{ initialTab: 'confirm' }} />} />
                    <Route path='/auth' element={<AuthPage />} />
                    <Route path='/logout' element={<LogoutPage />} />

                    {/* PUBLIC - privacy policy (App Store / marketing) */}
                    <Route path='/privacy' element={<ViewPrivacyPolicy />} />
                    
                    {/* TODO : view other's profiles */}
                    <Route path='/profile' element={<MyProfileView />} />
                    <Route path='/bills/default' element={<PrivateRouteWrapper Component={ViewDefaultBill} />} />
                    <Route path='/bills/templates/:id' element={<PrivateRouteWrapper Component={ViewTemplates} />} />
                    <Route path='/bills/templates' element={<PrivateRouteWrapper Component={ViewNewTemplate} />} />
                    <Route path='/statements' element={<PrivateRouteWrapper Component={Statements} />} />
                    <Route path='/dashboard' element={<PrivateRouteWrapper Component={ViewDashboard} />} />
                    <Route path='/' element={authClaims ? <ViewDashboard /> : <ViewLanding />} />
                </Routes>
            </InactivityForm>
        </>
    )
}

const PrivateRouteWrapper = ({ Component }) => {
    const { authClaims } = useContext(AuthContext);
    // Check context first, then localStorage as fallback
    // Read from localStorage on each render (not memoized) to catch updates
    let storedClaims = null;
    try {
        const stored = localStorage.getItem('authClaims');
        storedClaims = stored && stored !== 'undefined' ? JSON.parse(stored) : null;
    } catch {
        storedClaims = null;
    }

    // Check both context and localStorage for claims
    const hasClaims = authClaims || storedClaims;

    // Validate that session cookies are still valid
    // This ensures expired sessions don't allow access even if claims exist
    if (!hasClaims || !isSessionValid()) {
        // Clear stale claims if session is invalid
        if (storedClaims) {
            localStorage.removeItem('authClaims');
        }
        return <Navigate to="/auth" replace />
    }

    return <Component />
}


export default App;
